/**
 * Users Builder — config card + results panel.
 *
 * Layout:
 *   ┌──────────────────────────────────────────────┐
 *   │  TopBar: title + actions (Reset, Save, Run)  │
 *   ├──────────────────────────────────────────────┤
 *   │  Config Card (date range + filters)          │
 *   │  Summary KPI chips                           │
 *   │  User Cards Grid                             │
 *   │  Pagination                                  │
 *   └──────────────────────────────────────────────┘
 */
import { useState, useCallback, useEffect, useRef, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { format, parseISO } from 'date-fns';
import { getSectionCache, setSectionCache } from '@/lib/sectionCache';
import { useSavedReport } from '@/hooks/useSavedReport';
import { KpiCard } from '@/components/ui/KpiCard';
import { Pagination } from '@/components/ui/Pagination';
import {
  Play,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  X,
  Users,
  UserPlus,
  Crown,
  Activity,
  Layers,
  Clock,
  Eye,
  Signal,
  Megaphone,
  MapPin,
  ArrowUpRight,
  Hash,
  Shield,
  Smartphone,
  Monitor,
} from 'lucide-react';
import { cn } from '@/lib/cn';
import { Card } from '@/components/ui/Card';
import { Badge } from '@/components/ui/Badge';
import { Skeleton } from '@/components/ui/Skeleton';
import { EmptyState } from '@/components/ui/EmptyState';
import { DatePresetPicker } from '@/components/filters/DatePresetPicker';
import { FilterBuilder } from '@/components/filters/FilterBuilder';
import { BuilderShell } from '@/components/builder/BuilderShell';
import { api } from '@/services/api';
import { endpoints } from '@/services/endpoints';
import { toast } from '@/components/ui/Toast';
import { useUsersState } from './useUsersState';
import type { UsersState } from './useUsersState';
import {
  USERS_FILTER_FIELDS,
  USERS_FILTER_OPERATORS,
  USERS_FILTER_FIELD_META,
} from './users.constants';

// ─── Types ──────────────────────────────────────────────────────────────────

interface UserRow {
  user_id: string | null;
  user_pseudo_id: string;
  last_seen_time: string | null;
  first_open_time: string | null;
  platform: string | null;
  app_version: string | null;
  device_brand: string | null;
  device_model: string | null;
  device_marketing_name: string | null;
  device_os: string | null;
  device_os_version: string | null;
  device_language: string | null;
  device_timezone_offset: number | null;
  country: string | null;
  region: string | null;
  city: string | null;
  geo_sub_continent: string | null;
  is_premium: boolean | null;
  retention_days: number | null;
  app_open_count: number | null;
  analytics_id: string | null;
  device_id: string | null;
  privlock_access: string | null;
  gender: string | null;
  age: number | null;
  is_web_to_app_user: boolean | null;
  campaign_name: string | null;
  ad_group_name: string | null;
  traffic_source: string | null;
  event_session_ids: string[];
  advertising_id: string | null;
  app_install_source: string | null;
  clicked_push: string | null;
  environment: string | null;
  interest_areas_id: string | null;
  traffic_source_name: string | null;
  traffic_source_medium: string | null;
  traffic_source_source: string | null;
}

interface UsersSummary {
  totalUsers: number;
  platformBreakdown: Record<string, number>;
  premiumCount: number;
  freeCount: number;
  newUsersToday: number;
  topCountries: Array<{ country: string; count: number }>;
  trafficSourceBreakdown: Record<string, number>;
  avgRetentionDays: number | null;
  avgSessionsPerUser: number;
}

// ─── Helpers ────────────────────────────────────────────────────────────────

/** Country name → ISO 3166-1 alpha-2 for flagcdn.com */
const COUNTRY_CODES: Record<string, string> = {
  'Afghanistan': 'af', 'Albania': 'al', 'Algeria': 'dz', 'Argentina': 'ar',
  'Armenia': 'am', 'Australia': 'au', 'Austria': 'at', 'Azerbaijan': 'az',
  'Bahrain': 'bh', 'Bangladesh': 'bd', 'Belarus': 'by', 'Belgium': 'be',
  'Bolivia': 'bo', 'Bosnia & Herzegovina': 'ba', 'Brazil': 'br', 'Bulgaria': 'bg',
  'Cambodia': 'kh', 'Cameroon': 'cm', 'Canada': 'ca', 'Chile': 'cl',
  'China': 'cn', 'Colombia': 'co', 'Costa Rica': 'cr', 'Croatia': 'hr',
  'Czechia': 'cz', 'Denmark': 'dk', 'Dominican Republic': 'do', 'Ecuador': 'ec',
  'Egypt': 'eg', 'El Salvador': 'sv', 'Estonia': 'ee', 'Ethiopia': 'et',
  'Finland': 'fi', 'France': 'fr', 'Georgia': 'ge', 'Germany': 'de',
  'Ghana': 'gh', 'Greece': 'gr', 'Guatemala': 'gt', 'Honduras': 'hn',
  'Hong Kong': 'hk', 'Hungary': 'hu', 'Iceland': 'is', 'India': 'in',
  'Indonesia': 'id', 'Iran': 'ir', 'Iraq': 'iq', 'Ireland': 'ie',
  'Israel': 'il', 'Italy': 'it', 'Jamaica': 'jm', 'Japan': 'jp',
  'Jordan': 'jo', 'Kazakhstan': 'kz', 'Kenya': 'ke', 'Kuwait': 'kw',
  'Kyrgyzstan': 'kg', 'Laos': 'la', 'Latvia': 'lv', 'Lebanon': 'lb',
  'Libya': 'ly', 'Lithuania': 'lt', 'Luxembourg': 'lu', 'Malaysia': 'my',
  'Mexico': 'mx', 'Moldova': 'md', 'Mongolia': 'mn', 'Morocco': 'ma',
  'Myanmar (Burma)': 'mm', 'Nepal': 'np', 'Netherlands': 'nl', 'New Zealand': 'nz',
  'Nicaragua': 'ni', 'Nigeria': 'ng', 'North Macedonia': 'mk', 'Norway': 'no',
  'Oman': 'om', 'Pakistan': 'pk', 'Palestine': 'ps', 'Panama': 'pa',
  'Paraguay': 'py', 'Peru': 'pe', 'Philippines': 'ph', 'Poland': 'pl',
  'Portugal': 'pt', 'Puerto Rico': 'pr', 'Qatar': 'qa', 'Romania': 'ro',
  'Russia': 'ru', 'Saudi Arabia': 'sa', 'Senegal': 'sn', 'Serbia': 'rs',
  'Singapore': 'sg', 'Slovakia': 'sk', 'Slovenia': 'si', 'South Africa': 'za',
  'South Korea': 'kr', 'Spain': 'es', 'Sri Lanka': 'lk', 'Sudan': 'sd',
  'Sweden': 'se', 'Switzerland': 'ch', 'Syria': 'sy', 'Taiwan': 'tw',
  'Tanzania': 'tz', 'Thailand': 'th', 'Tunisia': 'tn', 'Turkey': 'tr',
  'Türkiye': 'tr', 'Uganda': 'ug', 'Ukraine': 'ua', 'United Arab Emirates': 'ae',
  'United Kingdom': 'gb', 'United States': 'us', 'Uruguay': 'uy',
  'Uzbekistan': 'uz', 'Venezuela': 've', 'Vietnam': 'vn', 'Yemen': 'ye',
  'Zambia': 'zm', 'Zimbabwe': 'zw',
};

function flagUrl(country: string | null): string {
  if (!country) return 'https://flagcdn.com/w40/xx.png';
  const code = COUNTRY_CODES[country];
  if (!code) return 'https://flagcdn.com/w40/xx.png';
  return `https://flagcdn.com/w40/${code}.png`;
}

function platformIconSrc(platform: string | null): string {
  if (platform === 'IOS') return '/icons/apple.svg';
  if (platform === 'ANDROID') return '/icons/android.svg';
  return '';
}

function formatTime(ts: string | null): string {
  if (!ts) return '-';
  try { return format(parseISO(ts), 'HH:mm:ss'); } catch { return '-'; }
}

function formatDateTime(ts: string | null): string {
  if (!ts) return '-';
  try { return format(parseISO(ts), 'MMM d, HH:mm:ss'); } catch { return '-'; }
}

function timezoneLabel(offsetSec: number | null): string {
  if (offsetSec == null) return '';
  const hrs = offsetSec / 3600;
  return `UTC${hrs >= 0 ? '+' : ''}${hrs}`;
}

function commaFmt(n: number): string {
  return n.toLocaleString();
}

/**
 * Deterministic hash → HSL color for a string value.
 * Produces consistent, readable pastel-ish colors.
 */
function hashColor(str: string): { bg: string; text: string; border: string } {
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    hash = str.charCodeAt(i) + ((hash << 5) - hash);
  }
  const hue = Math.abs(hash) % 360;
  return {
    bg: `hsl(${hue}, 65%, 95%)`,
    text: `hsl(${hue}, 60%, 35%)`,
    border: `hsl(${hue}, 50%, 85%)`,
  };
}

function accessVariant(access: string | null): 'success' | 'warning' | 'default' {
  if (!access) return 'default';
  if (access.startsWith('full')) return 'success';
  if (access.startsWith('limited')) return 'warning';
  return 'default';
}

// ─── Section cache ──────────────────────────────────────────────────────────

interface UsersCacheData {
  users: UserRow[];
  summary: UsersSummary | null;
  total: number;
  totalPages: number;
}

const CACHE_KEY = 'users-builder';

// ─── Main Component ─────────────────────────────────────────────────────────

const SECTION_ID = 'users';

export function UsersBuilder() {
  const { reportId } = useParams();
  const { state, update, reset, dates, toApiParams, toConfig, filterCount, searchParams } = useUsersState();

  // Restore cached data on mount
  const cached = useRef(getSectionCache<UsersCacheData>(CACHE_KEY));

  // Data — initialise from cache if available
  const [users, setUsers] = useState<UserRow[]>(cached.current?.users ?? []);
  const [summary, setSummary] = useState<UsersSummary | null>(cached.current?.summary ?? null);
  const [total, setTotal] = useState(cached.current?.total ?? 0);
  const [totalPages, setTotalPages] = useState(cached.current?.totalPages ?? 1);
  const [loading, setLoading] = useState(false);
  const [summaryLoading, setSummaryLoading] = useState(false);
  const [hasRun, setHasRun] = useState(!!cached.current);

  // Keep refs in sync for unmount save
  const dataRef = useRef<UsersCacheData>({ users, summary, total, totalPages });
  const hasRunRef = useRef(hasRun);
  useEffect(() => { dataRef.current = { users, summary, total, totalPages }; }, [users, summary, total, totalPages]);
  useEffect(() => { hasRunRef.current = hasRun; }, [hasRun]);

  // Save to cache on unmount
  useEffect(() => {
    return () => {
      if (hasRunRef.current) {
        setSectionCache(CACHE_KEY, dataRef.current);
      }
    };
  }, []);

  // ─── Load saved report ────────────────────────────────────────────────
  const { savedReport } = useSavedReport<UsersState>({
    reportId,
    sectionId: SECTION_ID,
    searchParams,
    update,
    mapConfig: useCallback((config: Record<string, unknown>) => ({
      preset: (config.preset as string) || 'today',
      start: (config.start as string) || '',
      end: (config.end as string) || '',
      filters: (config.filters as string) || '',
    }), []),
  });

  // ─── Run Query ────────────────────────────────────────────────────────
  const runQuery = useCallback(async () => {
    setLoading(true);
    setSummaryLoading(true);
    setHasRun(true);
    try {
      const params = toApiParams();
      const [usersRes, summaryRes] = await Promise.all([
        api.getWithMeta<UserRow[]>(endpoints.users.list(), params),
        api.get<UsersSummary>(endpoints.users.summary(), {
          startDate: dates.startDate,
          endDate: dates.endDate,
        }),
      ]);

      setUsers(usersRes.data ?? []);
      if (usersRes.meta) {
        setTotal(usersRes.meta.total as number);
        setTotalPages(usersRes.meta.totalPages as number);
      }
      setSummary(summaryRes);
    } catch (err) {
      console.error('Failed to fetch users:', err);
      toast.error('Failed to load users');
    } finally {
      setLoading(false);
      setSummaryLoading(false);
    }
  }, [toApiParams, dates]);

  // Auto-run on page change (but not initial load)
  const prevPageRef = useRef(state.page);
  useEffect(() => {
    if (hasRun && state.page !== prevPageRef.current) {
      prevPageRef.current = state.page;
      // Re-fetch only users (not summary) for pagination
      setLoading(true);
      const params = toApiParams();
      api
        .getWithMeta<UserRow[]>(endpoints.users.list(), params)
        .then((res) => {
          setUsers(res.data ?? []);
          if (res.meta) {
            setTotal(res.meta.total as number);
            setTotalPages(res.meta.totalPages as number);
          }
        })
        .catch(console.error)
        .finally(() => setLoading(false));
    }
  }, [state.page, hasRun, toApiParams]);

  // Auto-run on first load if saved report
  useEffect(() => {
    if (savedReport && !hasRun) {
      runQuery();
    }
  }, [savedReport, hasRun, runQuery]);

  // Inline session lookup (replaces user list when a user is selected)
  const [selectedUser, setSelectedUser] = useState<UserRow | null>(null);

  const configContent = (
    <div className="px-5 pb-5 border-t border-slate-100 pt-4 space-y-4">
      <div className="flex items-center gap-4">
        <DatePresetPicker
          value={state.preset}
          onChange={(preset) => update({ preset })}
          startDate={state.start}
          endDate={state.end}
          onDateChange={(start, end) => update({ start, end, preset: 'custom' })}
        />
      </div>
      <FilterBuilder
        value={state.filters}
        onChange={(filters) => update({ filters })}
        fields={USERS_FILTER_FIELDS}
        operators={USERS_FILTER_OPERATORS}
        fieldMeta={USERS_FILTER_FIELD_META}
      />
    </div>
  );

  return (
    <BuilderShell
      title="Users"
      subtitle={savedReport?.report?.description || 'Browse active users'}
      sectionId={SECTION_ID}
      sectionRoute="/users"
      reportId={reportId}
      savedReport={savedReport as { report?: { name?: string; description?: string } } | undefined}
      onReset={reset}
      onRun={runQuery}
      isRunning={loading}
      configForSave={toConfig()}
      configPanel={configContent}
    >
      {!hasRun ? (
        <Card>
          <EmptyState
            icon={Play}
            title="Configure and run"
            description="Set your date range and filters above, then click Run to load users"
          />
        </Card>
      ) : selectedUser ? (
        <UserSessionPanel
          user={selectedUser}
          onBack={() => setSelectedUser(null)}
        />
      ) : (
        <>
          {/* Summary KPIs */}
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3">
            <KpiCard label="Total Users" value={summary ? commaFmt(summary.totalUsers) : null} icon={<Users className="w-4 h-4" />} color="#0EA5E9" loading={summaryLoading} />
            <KpiCard label="New Users" value={summary ? commaFmt(summary.newUsersToday) : null} icon={<UserPlus className="w-4 h-4" />} color="#10B981" loading={summaryLoading} />
            <KpiCard label="Premium" value={summary ? commaFmt(summary.premiumCount) : null} icon={<Crown className="w-4 h-4" />} color="#F59E0B" loading={summaryLoading} />
            <KpiCard label="Avg Retention" value={summary?.avgRetentionDays != null ? `${summary.avgRetentionDays}d` : '-'} icon={<Activity className="w-4 h-4" />} color="#8B5CF6" loading={summaryLoading} />
            <KpiCard label="Avg Sessions" value={summary ? String(summary.avgSessionsPerUser) : null} icon={<Layers className="w-4 h-4" />} color="#EC4899" loading={summaryLoading} />
          </div>

          {!loading && (
            <div className="text-xs text-slate-500">
              Showing <span className="font-semibold text-slate-700">{commaFmt(total)}</span> users
            </div>
          )}

          {loading ? (
            <div className="space-y-2">
              {Array.from({ length: 8 }).map((_, i) => (
                <UserCardSkeleton key={i} />
              ))}
            </div>
          ) : users.length === 0 ? (
            <Card>
              <EmptyState icon={Users} title="No users found" description="Try adjusting your filters or date range" />
            </Card>
          ) : (
            <>
              <div className="space-y-2">
                <AnimatePresence mode="popLayout">
                  {users.map((user, idx) => (
                    <motion.div
                      key={user.user_pseudo_id + (user.user_id || idx)}
                      initial={{ opacity: 0, y: 6 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: -6 }}
                      transition={{ duration: 0.12, delay: Math.min(idx * 0.015, 0.2) }}
                    >
                      <UserCard user={user} onClick={() => setSelectedUser(user)} />
                    </motion.div>
                  ))}
                </AnimatePresence>
              </div>

              {totalPages > 1 && (
                <Pagination page={state.page} totalPages={totalPages} onPageChange={(p) => update({ page: p })} />
              )}
            </>
          )}
        </>
      )}
    </BuilderShell>
  );
}

// ─── Sub-Components ─────────────────────────────────────────────────────────

// KpiCard imported from shared components

function UserCard({ user, onClick }: { user: UserRow; onClick: () => void }) {
  const isNew = user.retention_days === 1;

  return (
    <Card
      interactive
      padding="none"
      className={cn('group', user.is_premium && 'ring-2 ring-amber-400 bg-amber-50/60')}
      onClick={onClick}
    >
      <div className="flex items-center gap-4 px-4 py-3">
        {/* Flag + Location */}
        <div className="flex items-center gap-2.5 w-40 shrink-0">
          <img
            src={flagUrl(user.country)}
            alt={user.country ?? ''}
            className="w-7 h-5 rounded-sm object-cover shadow-sm border border-slate-200/60"
            loading="lazy"
          />
          <div className="min-w-0">
            <span className="text-xs font-semibold text-slate-900 truncate block">
              {user.country ?? 'Unknown'}
            </span>
            {user.city && <span className="text-2xs text-slate-400 truncate block">{user.city}</span>}
          </div>
        </div>

        {/* Platform */}
        <div className="flex items-center gap-1.5 w-24 shrink-0">
          {platformIconSrc(user.platform) ? (
            <img src={platformIconSrc(user.platform)} alt={user.platform ?? ''} className="w-3.5 h-3.5 opacity-50" />
          ) : (
            <Monitor className="w-3.5 h-3.5 text-slate-400" />
          )}
          <span className="text-2xs text-slate-600">
            {user.platform === 'IOS' ? 'iOS' : user.platform === 'ANDROID' ? 'Android' : 'Unknown'}
          </span>
        </div>

        {/* Times */}
        <div className="flex items-center gap-4 w-44 shrink-0">
          <div className="text-center">
            <p className="text-2xs text-slate-400">First open</p>
            <p className="text-xs font-medium text-slate-700 tabular-nums">{formatTime(user.first_open_time)}</p>
          </div>
          <div className="text-center">
            <p className="text-2xs text-slate-400">Last seen</p>
            <p className="text-xs font-medium text-slate-700 tabular-nums">{formatTime(user.last_seen_time)}</p>
          </div>
        </div>

        {/* Acquisition chips */}
        <div className="flex items-center gap-1.5 flex-1 min-w-0 flex-wrap">
          {user.traffic_source && <ColorChip value={user.traffic_source} />}
          {user.campaign_name && <ColorChip value={user.campaign_name} />}
          {user.ad_group_name && <ColorChip value={user.ad_group_name} />}
        </div>

        {/* Stats */}
        <div className="flex items-center gap-3 shrink-0">
          <MiniStat label="Sessions" value={user.app_open_count != null ? String(user.app_open_count) : '-'} />
          <MiniStat label="Retention" value={user.retention_days != null ? `${user.retention_days}d` : '-'} />
          {user.app_version && <MiniStat label="Version" value={`v${user.app_version}`} />}
        </div>

        {/* Badges */}
        <div className="flex items-center gap-1.5 shrink-0">
          {isNew && <Badge variant="success" dot>New</Badge>}
        </div>

        {/* Arrow */}
        <ArrowUpRight className="w-3.5 h-3.5 text-slate-300 group-hover:text-sky-500 transition-colors shrink-0" />
      </div>
    </Card>
  );
}

/** Color-highlighted chip for source/campaign/ad-group values */
function ColorChip({ value }: { value: string }) {
  const c = hashColor(value);
  return (
    <span
      className="inline-flex items-center px-2 py-0.5 rounded-md text-2xs font-medium whitespace-nowrap"
      style={{ background: c.bg, color: c.text, border: `1px solid ${c.border}` }}
      title={value}
    >
      {value}
    </span>
  );
}

function MiniStat({ label, value }: { label: string; value: string }) {
  return (
    <div className="text-center">
      <p className="text-xs font-semibold text-slate-700">{value}</p>
      <p className="text-2xs text-slate-400">{label}</p>
    </div>
  );
}

function UserCardSkeleton() {
  return (
    <Card padding="none">
      <div className="flex items-center gap-4 px-4 py-3">
        <div className="flex items-center gap-2.5 w-40 shrink-0">
          <Skeleton className="w-7 h-5 rounded-sm" />
          <div className="space-y-1">
            <Skeleton className="h-3 w-20" />
            <Skeleton className="h-2.5 w-14" />
          </div>
        </div>
        <Skeleton className="h-3 w-16 shrink-0" />
        <Skeleton className="h-3 w-32 shrink-0" />
        <div className="flex-1 flex gap-1.5">
          <Skeleton className="h-5 w-20 rounded-md" />
          <Skeleton className="h-5 w-24 rounded-md" />
        </div>
        <Skeleton className="h-3 w-24 shrink-0" />
      </div>
    </Card>
  );
}

// ─── Session Lookup Panel (replaces user list when user is selected) ────────

interface SessionRow {
  user_pseudo_id: string;
  user_id: string | null;
  event_session_id: string;
  session_start_event_name: string | null;
  session_start_timestamp: number;
  session_start_time: { value: string } | string;
  session_end_timestamp: number;
  session_end_time: { value: string } | string;
  session_duration_seconds: number | null;
  event_count: number;
}

interface SessionEvent {
  event_name: string;
  event_timestamp: number;
  event_time: { value: string } | string;
  params: Array<{
    key: string;
    string_value: string | null;
    int_value: number | null;
    double_value: number | null;
  }> | null;
}

function parseBqTimestamp(ts: { value: string } | string | null): string {
  if (!ts) return '-';
  const raw = typeof ts === 'object' && 'value' in ts ? ts.value : ts;
  try { return format(parseISO(raw), 'HH:mm:ss'); } catch { return raw; }
}

function parseBqDateTime(ts: { value: string } | string | null): string {
  if (!ts) return '-';
  const raw = typeof ts === 'object' && 'value' in ts ? ts.value : ts;
  try { return format(parseISO(raw), 'MMM d, HH:mm'); } catch { return raw; }
}

function formatDuration(seconds: number | null): string {
  if (seconds == null) return '-';
  if (seconds < 60) return `${Math.round(seconds)}s`;
  const m = Math.floor(seconds / 60);
  const s = Math.round(seconds % 60);
  return `${m}m ${s}s`;
}

/** Convert a Date to YYYYMMDD */
function dateToSuffix(d: Date): string {
  const yyyy = d.getFullYear().toString();
  const mm = (d.getMonth() + 1).toString().padStart(2, '0');
  const dd = d.getDate().toString().padStart(2, '0');
  return `${yyyy}${mm}${dd}`;
}

/** Get tight sinceDate/untilDate from a session start time (±1 day) */
function sessionDateRange(ts: { value: string } | string | null): { sinceDate: string; untilDate: string } | undefined {
  if (!ts) return undefined;
  const raw = typeof ts === 'object' && 'value' in ts ? ts.value : ts;
  try {
    const d = parseISO(raw);
    const before = new Date(d); before.setDate(before.getDate() - 1);
    const after = new Date(d); after.setDate(after.getDate() + 1);
    return { sinceDate: dateToSuffix(before), untilDate: dateToSuffix(after) };
  } catch {
    return undefined;
  }
}

/** Convert an ISO date string to YYYYMMDD for _TABLE_SUFFIX filter */
function toSinceDate(isoString: string | null): string | undefined {
  if (!isoString) return undefined;
  try {
    const d = parseISO(isoString);
    // subtract 1 day safety margin
    d.setDate(d.getDate() - 1);
    const yyyy = d.getFullYear().toString();
    const mm = (d.getMonth() + 1).toString().padStart(2, '0');
    const dd = d.getDate().toString().padStart(2, '0');
    return `${yyyy}${mm}${dd}`;
  } catch {
    return undefined;
  }
}

function UserSessionPanel({
  user,
  onBack,
}: {
  user: UserRow;
  onBack: () => void;
}) {
  const [sessions, setSessions] = useState<SessionRow[]>([]);
  const [sessionsLoading, setSessionsLoading] = useState(true);
  const [expandedSession, setExpandedSession] = useState<string | null>(null);

  const sinceDate = toSinceDate(user.first_open_time);

  // Fetch all sessions for this user (bounded by first_open_time)
  useEffect(() => {
    let cancelled = false;
    setSessionsLoading(true);
    setSessions([]);

    const params: Record<string, string> = { userPseudoId: user.user_pseudo_id };
    if (sinceDate) params.sinceDate = sinceDate;

    api
      .get<SessionRow[]>(endpoints.users.sessions(), params)
      .then((data) => {
        if (!cancelled) setSessions(data);
      })
      .catch(() => {
        if (!cancelled) toast.error('Failed to load sessions');
      })
      .finally(() => {
        if (!cancelled) setSessionsLoading(false);
      });

    return () => { cancelled = true; };
  }, [user.user_pseudo_id, sinceDate]);

  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.2 }}
    >
      {/* Back button + user summary header */}
      <Card className={cn(user.is_premium && 'ring-2 ring-amber-400 bg-amber-50/40')}>
        <div className="flex items-start gap-4">
          <button
            onClick={onBack}
            className="mt-0.5 p-1.5 rounded-lg hover:bg-slate-100 transition-colors shrink-0"
          >
            <ChevronLeft className="w-5 h-5 text-slate-500" />
          </button>

          <div className="flex-1 min-w-0 space-y-3">
            {/* Identity row */}
            <div className="flex items-center gap-3 flex-wrap">
              <img
                src={flagUrl(user.country)}
                alt={user.country ?? ''}
                className="w-8 h-6 rounded-sm object-cover shadow-sm border border-slate-200/60"
              />
              <div>
                <div className="flex items-center gap-2">
                  <h2 className="text-sm font-bold text-slate-900">{user.country ?? 'Unknown'}</h2>
                  {user.city && (
                    <span className="text-xs text-slate-400">
                      {user.city}{user.region ? `, ${user.region}` : ''}
                    </span>
                  )}
                </div>
                <div className="flex items-center gap-2 mt-0.5">
                  {platformIconSrc(user.platform) ? (
                    <img src={platformIconSrc(user.platform)} alt="" className="w-3.5 h-3.5 opacity-50" />
                  ) : (
                    <Monitor className="w-3.5 h-3.5 text-slate-400" />
                  )}
                  <span className="text-2xs text-slate-500">
                    {user.platform === 'IOS' ? 'iOS' : user.platform === 'ANDROID' ? 'Android' : 'Unknown'}
                  </span>
                  {user.device_marketing_name && (
                    <>
                      <span className="text-slate-300">·</span>
                      <span className="text-2xs text-slate-400">{user.device_brand} {user.device_marketing_name}</span>
                    </>
                  )}
                </div>
              </div>

              {/* Badges */}
              <div className="flex items-center gap-1.5 ml-auto">
                {user.is_premium && <Badge variant="warning" dot>Premium</Badge>}
                {user.is_web_to_app_user && <Badge variant="brand">Web-to-App</Badge>}
                {user.retention_days === 1 && <Badge variant="success" dot>New</Badge>}
              </div>
            </div>

            {/* Info chips row */}
            <div className="flex items-center gap-4 text-2xs text-slate-500 flex-wrap">
              <span className="flex items-center gap-1">
                <Hash className="w-3 h-3 text-slate-400" />
                <code className="text-slate-600 font-mono">{user.user_pseudo_id.slice(0, 20)}…</code>
              </span>
              {user.user_id && (
                <span className="flex items-center gap-1">
                  <Users className="w-3 h-3 text-slate-400" />
                  <code className="text-slate-600 font-mono text-2xs">{user.user_id}</code>
                </span>
              )}
              <span className="flex items-center gap-1">
                <Clock className="w-3 h-3 text-slate-400" />
                First open: {formatDateTime(user.first_open_time)}
              </span>
              <span className="flex items-center gap-1">
                <Eye className="w-3 h-3 text-slate-400" />
                Last seen: {formatDateTime(user.last_seen_time)}
              </span>
              {user.traffic_source && <ColorChip value={user.traffic_source} />}
              {user.campaign_name && <ColorChip value={user.campaign_name} />}
            </div>
          </div>
        </div>
      </Card>

      {/* Sessions list */}
      <div className="mt-4">
        <div className="flex items-center gap-2 mb-3">
          <Signal className="w-4 h-4 text-slate-400" />
          <h3 className="text-sm font-semibold text-slate-900">
            Sessions {!sessionsLoading && `(${sessions.length})`}
          </h3>
        </div>

        {sessionsLoading ? (
          <div className="space-y-2">
            {Array.from({ length: 3 }).map((_, i) => (
              <Card key={i} padding="sm">
                <div className="flex items-center gap-4">
                  <Skeleton className="h-4 w-4 rounded" />
                  <Skeleton className="h-4 w-32" />
                  <Skeleton className="h-4 w-20" />
                  <Skeleton className="h-4 w-16 ml-auto" />
                </div>
              </Card>
            ))}
          </div>
        ) : sessions.length === 0 ? (
          <Card>
            <EmptyState icon={Signal} title="No sessions found" description="This user has no session data" />
          </Card>
        ) : (
          <div className="space-y-2">
            <AnimatePresence initial={false}>
              {sessions.map((session, idx) => (
                <SessionAccordion
                  key={session.event_session_id}
                  session={session}
                  index={idx}
                  isExpanded={expandedSession === session.event_session_id}
                  onToggle={() =>
                    setExpandedSession((prev) =>
                      prev === session.event_session_id ? null : session.event_session_id,
                    )
                  }
                  userPseudoId={user.user_pseudo_id}
                  sinceDate={sinceDate}
                />
              ))}
            </AnimatePresence>
          </div>
        )}
      </div>
    </motion.div>
  );
}

// ─── Session Accordion ──────────────────────────────────────────────────────

function SessionAccordion({
  session,
  index,
  isExpanded,
  onToggle,
  userPseudoId,
  sinceDate,
}: {
  session: SessionRow;
  index: number;
  isExpanded: boolean;
  onToggle: () => void;
  userPseudoId: string;
  sinceDate?: string;
}) {
  const [events, setEvents] = useState<SessionEvent[]>([]);
  const [eventsLoading, setEventsLoading] = useState(false);
  const [eventsFetched, setEventsFetched] = useState(false);

  // Fetch events on first expand — use tight date range from session start time
  const sessionRange = sessionDateRange(session.session_start_time);

  useEffect(() => {
    if (!isExpanded || eventsFetched) return;
    let cancelled = false;
    setEventsLoading(true);

    const params: Record<string, string> = {
      userPseudoId,
      sessionId: session.event_session_id,
    };
    if (sessionRange) {
      params.sinceDate = sessionRange.sinceDate;
      params.untilDate = sessionRange.untilDate;
    } else if (sinceDate) {
      params.sinceDate = sinceDate;
    }

    api
      .get<SessionEvent[]>(endpoints.users.sessionEvents(), params)
      .then((data) => {
        if (!cancelled) {
          setEvents(data);
          setEventsFetched(true);
        }
      })
      .catch(() => {
        if (!cancelled) toast.error('Failed to load events');
      })
      .finally(() => {
        if (!cancelled) setEventsLoading(false);
      });

    return () => { cancelled = true; };
  }, [isExpanded, eventsFetched, userPseudoId, session.event_session_id, sessionRange, sinceDate]);

  return (
    <motion.div
      initial={{ opacity: 0, y: 6 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.12, delay: Math.min(index * 0.02, 0.15) }}
    >
      <Card padding="none" className={cn(isExpanded && 'ring-1 ring-sky-200')}>
        {/* Session header row — always visible */}
        <button
          onClick={onToggle}
          className="w-full flex items-center gap-3 px-4 py-3 text-left hover:bg-slate-50/60 transition-colors"
        >
          <motion.div
            animate={{ rotate: isExpanded ? 90 : 0 }}
            transition={{ duration: 0.15 }}
          >
            <ChevronRight className="w-4 h-4 text-slate-400" />
          </motion.div>

          <span className="text-2xs font-medium text-slate-500 bg-slate-100 px-1.5 py-0.5 rounded tabular-nums">
            #{index + 1}
          </span>

          <span className="text-xs font-mono text-slate-600">
            {session.event_session_id}
          </span>

          <div className="flex items-center gap-4 ml-auto shrink-0 text-2xs text-slate-500">
            <span className="flex items-center gap-1 tabular-nums">
              <Clock className="w-3 h-3 text-slate-400" />
              {parseBqDateTime(session.session_start_time)}
            </span>
            <span className="flex items-center gap-1 tabular-nums">
              <Activity className="w-3 h-3 text-slate-400" />
              {formatDuration(session.session_duration_seconds)}
            </span>
            <span className="flex items-center gap-1">
              <Layers className="w-3 h-3 text-slate-400" />
              {session.event_count} events
            </span>
            {session.session_start_event_name && (
              <Badge variant="default" className="text-2xs">
                {session.session_start_event_name}
              </Badge>
            )}
          </div>
        </button>

        {/* Expanded events list */}
        <AnimatePresence initial={false}>
          {isExpanded && (
            <motion.div
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: 'auto', opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              transition={{ duration: 0.2, ease: [0.23, 1, 0.32, 1] }}
              className="overflow-hidden"
            >
              <div className="border-t border-slate-100 px-4 py-3">
                {eventsLoading ? (
                  <div className="space-y-2 py-2">
                    {Array.from({ length: 4 }).map((_, i) => (
                      <div key={i} className="flex items-center gap-3">
                        <Skeleton className="w-2 h-2 rounded-full" />
                        <Skeleton className="h-3.5 w-16" />
                        <Skeleton className="h-3.5 w-32" />
                      </div>
                    ))}
                  </div>
                ) : events.length === 0 ? (
                  <p className="text-xs text-slate-400 py-2">No events found for this session</p>
                ) : (
                  <div className="relative">
                    {/* Timeline line */}
                    <div className="absolute left-[5px] top-2 bottom-2 w-px bg-slate-200" />

                    <div className="space-y-0">
                      {events.map((event, i) => (
                        <EventRow key={`${event.event_name}-${event.event_timestamp}-${i}`} event={event} index={i} />
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </Card>
    </motion.div>
  );
}

// ─── Event Row ──────────────────────────────────────────────────────────────

/** Color palette for event names */
const EVENT_COLORS: Record<string, string> = {
  app_launch: '#0EA5E9',
  app_first_opened: '#10B981',
  purchase_success: '#F59E0B',
  purchase_started: '#F97316',
  purchase_failed: '#EF4444',
  paywall_opened: '#8B5CF6',
  paywall_closed: '#A78BFA',
  push_authorized: '#10B981',
  push_unauthorized: '#EF4444',
  app_backgrounded: '#94A3B8',
  app_resumed: '#64748B',
  onboarding_completed: '#10B981',
};

function getEventColor(name: string): string {
  if (EVENT_COLORS[name]) return EVENT_COLORS[name];
  // Hash-based fallback
  let hash = 0;
  for (let i = 0; i < name.length; i++) hash = name.charCodeAt(i) + ((hash << 5) - hash);
  return `hsl(${Math.abs(hash) % 360}, 55%, 50%)`;
}

function EventRow({ event, index }: { event: SessionEvent; index: number }) {
  const [showParams, setShowParams] = useState(false);
  const color = getEventColor(event.event_name);
  const hasParams = event.params && event.params.length > 0;

  // Filter out the session-level params for cleaner display
  const meaningfulParams = useMemo(() => {
    if (!event.params) return [];
    return event.params.filter(
      (p) => !['event_session_id', 'session_start_time', 'ga_session_id', 'ga_session_number', 'engaged_session_event'].includes(p.key),
    );
  }, [event.params]);

  return (
    <motion.div
      initial={{ opacity: 0, x: -4 }}
      animate={{ opacity: 1, x: 0 }}
      transition={{ duration: 0.1, delay: Math.min(index * 0.01, 0.3) }}
      className="relative pl-6 py-1.5 group"
    >
      {/* Timeline dot */}
      <div
        className="absolute left-[2px] top-[11px] w-[7px] h-[7px] rounded-full border-2 border-white"
        style={{ backgroundColor: color }}
      />

      <div className="flex items-center gap-2 min-w-0">
        <span className="text-2xs text-slate-400 tabular-nums w-14 shrink-0">
          {parseBqTimestamp(event.event_time)}
        </span>

        <span
          className="text-xs font-medium px-1.5 py-0.5 rounded"
          style={{
            color,
            backgroundColor: `${color}12`,
          }}
        >
          {event.event_name}
        </span>

        {/* Inline preview of key params */}
        {meaningfulParams.length > 0 && (
          <button
            onClick={() => setShowParams(!showParams)}
            className="text-2xs text-slate-400 hover:text-slate-600 transition-colors flex items-center gap-0.5"
          >
            <span>{meaningfulParams.length} params</span>
            <ChevronDown className={cn('w-3 h-3 transition-transform', showParams && 'rotate-180')} />
          </button>
        )}
      </div>

      {/* Expanded params */}
      <AnimatePresence>
        {showParams && meaningfulParams.length > 0 && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.15 }}
            className="overflow-hidden"
          >
            <div className="mt-1.5 ml-14 bg-slate-50 rounded-lg px-3 py-2 space-y-1">
              {meaningfulParams.map((p) => {
                const val = p.string_value ?? p.int_value ?? p.double_value ?? '—';
                return (
                  <div key={p.key} className="flex items-baseline gap-2 text-2xs">
                    <span className="text-slate-400 font-mono shrink-0">{p.key}</span>
                    <span className="text-slate-300">=</span>
                    <span className="text-slate-700 font-mono break-all">{String(val)}</span>
                  </div>
                );
              })}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
}

function getPageNumbers(current: number, total: number): (number | '...')[] {
  if (total <= 7) return Array.from({ length: total }, (_, i) => i + 1);
  const pages: (number | '...')[] = [1];
  if (current > 3) pages.push('...');
  const start = Math.max(2, current - 1);
  const end = Math.min(total - 1, current + 1);
  for (let i = start; i <= end; i++) pages.push(i);
  if (current < total - 2) pages.push('...');
  pages.push(total);
  return pages;
}
